"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateSectionGroup = validateSectionGroup;
exports.validateSection = validateSection;
exports.validateSubsection = validateSubsection;
exports.validateFieldGroup = validateFieldGroup;
exports.validateField = validateField;
const lodash_1 = __importDefault(require("lodash"));
const documentType_1 = require("../types/carrierQuestionnaire/documentType");
const fieldTypes_1 = require("../types/carrierQuestionnaire/fieldTypes");
const structure_1 = require("./structure");
const validations_1 = require("./validations");
function validateIsString(propertyKey, property) {
    if (typeof property !== 'string') {
        throw Error(`property ${String(propertyKey)} must be defined as a string`);
    }
    return true;
}
function validateIsNumber(propertyKey, property) {
    if (typeof property !== 'number') {
        throw Error(`property ${String(propertyKey)} must be defined as a number`);
    }
    return true;
}
function validateIsArrayOfType(thing, key, type) {
    const value = thing[key];
    if (!Array.isArray(value)) {
        throw Error(`property ${String(key)} must be defined as an array`);
    }
    if (value.every((e) => typeof e === type)) {
        return true;
    }
    throw Error(`property ${String(key)} elements are not of type ${type}`);
}
function validateExistenceOf(propertyKey, property) {
    if (typeof property === 'undefined') {
        throw Error(`property ${String(propertyKey)} must be defined`);
    }
    return true;
}
function validateIsLocalizable(propertyKey, property) {
    validateExistenceOf(propertyKey, property);
    if (typeof property.en === 'undefined' || typeof property.fr === 'undefined') {
        throw Error(`property ${String(propertyKey)} must have text for both english and french`);
    }
    return true;
}
function validateIsDocumentTypes(documentTypes) {
    const allowedDocumentTypes = Object.values(documentType_1.PdfDocumentTypes);
    const allDocumentTypesAreValid = documentTypes.every((documentType) => allowedDocumentTypes.includes(documentType));
    if (!allDocumentTypesAreValid) {
        throw Error(`invalid PdfDocumentType provided; allowed values are: ${allowedDocumentTypes.join(', ')}`);
    }
    return allDocumentTypesAreValid;
}
function validateIsValidations(validation) {
    const allowedValidations = Object.values(validations_1.Validations);
    if (!allowedValidations.includes(validation)) {
        throw Error(`invalid value "${validation}" used for Validations. Use one of the valid validation types: ${allowedValidations.join(', ')}`);
    }
    return true;
}
function validateBaseNode(node) {
    return validateExistenceOf('id', node.id);
}
function validateRepeatableOptions(options) {
    if ((0, structure_1.isRepeatableOptionsBasedOnCollection)(options)) {
        return validateRepeatableOptionsBasedOnCollection(options);
    }
    return validateRepeatableOptionsWithLimits(options);
}
function validateRepeatableOptionsWithLimits(options) {
    try {
        const { repeatable, minRepetitions, maxRepetitions } = options;
        return (validateIsNumber('minRepetitions', minRepetitions) &&
            validateIsNumber('maxRepetitions', maxRepetitions) &&
            validateExistenceOf('repeatable', repeatable));
    }
    catch (error) {
        throw Error(`invalid repeatableOptionsWithLimits options: ${error.message}`);
    }
}
function validateRepeatableOptionsBasedOnCollection(options) {
    try {
        const { repeatable, expandToCollectionLength, selectText, selectTitle } = options;
        return (validateExistenceOf('repeatable', repeatable) &&
            validateExistenceOf('expandToCollectionLength', expandToCollectionLength) &&
            validateIsString('expandToCollectionLength', expandToCollectionLength) &&
            (!selectText || validateIsArrayOfType(options, 'selectText', 'string')) &&
            (!selectTitle || validateIsArrayOfType(options, 'selectTitle', 'string')));
    }
    catch (error) {
        throw Error(`invalid repeatableOptionsBasedOnCollection options: ${error.message}`);
    }
}
function validateSectionGroup(node) {
    try {
        let isValidRepeatableSectionGroup = true;
        if ((0, structure_1.isSectionGroupRepeatable)(node)) {
            isValidRepeatableSectionGroup =
                validateExistenceOf('nodeId', node.nodeId) && validateRepeatableOptions(node.options);
        }
        return (isValidRepeatableSectionGroup &&
            validateBaseNode(node) &&
            validateIsString('index', node.index) &&
            validateIsLocalizable('title', node.title) &&
            validateExistenceOf('sections', node.sections) &&
            validateSections(node.sections));
    }
    catch (error) {
        throw Error(`Invalid section group with id ${node.id}: ${error.message}`);
    }
}
function validateSections(nodes) {
    return nodes.every((section) => validateSection(section));
}
function validateSection(node) {
    try {
        return (validateBaseNode(node) &&
            validateIsString('index', node.index) &&
            validateIsLocalizable('title', node.title) &&
            validateIsDocumentTypes(node.documentTypes) &&
            validateExistenceOf('subsections', node.subsections) &&
            validateSubsections(node.subsections));
    }
    catch (error) {
        throw Error(`Invalid section with id ${node.id}: ${error.message}`);
    }
}
function validateSubsections(nodes) {
    return nodes.every((subsection) => validateSubsection(subsection));
}
function validateSubsection(node) {
    try {
        return (validateBaseNode(node) &&
            validateIsString('index', node.index) &&
            validateIsLocalizable('title', node.title) &&
            validateExistenceOf('fieldGroups', node.fieldGroups) &&
            validateFieldGroups(node.fieldGroups));
    }
    catch (error) {
        throw Error(`Invalid subsection with id ${node.id}: ${error.message}`);
    }
}
function validateBaseFieldGroup(node) {
    return (validateBaseNode(node) && validateExistenceOf('fields', node.fields) && validateFields(node.fields));
}
function validateFieldGroups(nodes) {
    return nodes.every((fieldGroup) => validateFieldGroup(fieldGroup));
}
function validateFieldGroup(node) {
    try {
        let repeatableFieldGroupIsValid = true;
        if ((0, structure_1.isFieldGroupRepeatable)(node)) {
            repeatableFieldGroupIsValid =
                validateExistenceOf('removeQuestionButtonText', node.removeQuestionButtonText) &&
                    validateExistenceOf('addQuestionButtonText', node.addQuestionButtonText) &&
                    validateExistenceOf('nodeId', node.nodeId) &&
                    validateRepeatableOptions(node.options);
        }
        return repeatableFieldGroupIsValid && validateBaseFieldGroup(node);
    }
    catch (error) {
        throw Error(`Invalid fieldGroup with id ${node.id}: ${error.message}`);
    }
}
function validateFields(nodes) {
    return nodes.every((field) => validateField(field));
}
function validateBaseField(node) {
    if ((0, structure_1.isReadOnlyField)(node)) {
        if (!structure_1.supportedReadOnlyFieldTypes.includes(node.type)) {
            throw new Error(`Invalid read only field type "${node.type}" for field with id "${node.id}". Valid field types are: ${structure_1.supportedReadOnlyFieldTypes.join(', ')}`);
        }
    }
    else {
        const nonReadOnlyFieldTypes = lodash_1.default.filter(Object.values(fieldTypes_1.FieldTypes), (type) => !structure_1.readOnlyFieldTypes.includes(type));
        if (!nonReadOnlyFieldTypes.includes(node.type)) {
            throw new Error(`Invalid field type "${node.type}" for field with id "${node.id}". Make sure to add readOnly: true if using a read only field type. Valid field types are: ${nonReadOnlyFieldTypes.join(', ')}`);
        }
    }
    return (validateBaseNode(node) &&
        validateIsString('type', node.type) &&
        validateIsValidations(node.validation.type));
}
function validateField(node) {
    let fieldIsValid;
    try {
        if ((0, structure_1.isOptionField)(node)) {
            fieldIsValid = validateOptionField(node);
        }
        else if ((0, structure_1.isAgreeField)(node)) {
            fieldIsValid = validateAgreeField(node);
        }
        else {
            fieldIsValid = validateBaseField(node);
        }
        return validateBaseNode(node) && validateExistenceOf('nodeId', node.nodeId) && fieldIsValid;
    }
    catch (error) {
        throw Error(`Invalid field with id ${node.id}: ${error.message}`);
    }
}
function validateAgreeField(node) {
    return (validateExistenceOf('label', node.label) &&
        validateExistenceOf('confirmedLabel', node.confirmedLabel) &&
        validateExistenceOf('modalHeader', node.modalHeader) &&
        validateExistenceOf('modalText', node.modalText));
}
function validateOptionField(node) {
    return validateBaseField(node) && validateSelectOptions(node.options);
}
function validateSelectOption(node) {
    return validateBaseNode(node) && validateIsLocalizable('text', node.text);
}
function validateSelectOptions(nodes) {
    return nodes.every((node) => validateSelectOption(node));
}
